import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Gavel, Clock3, ArrowUpRight } from "lucide-react";
import Navbar from "../components/Navbar";
import api from "../api/client";

function timeLeft(endsAt) {
    if (!endsAt) return null;
    const diff = Math.max(0, new Date(endsAt).getTime() - Date.now());
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff / 3600000) % 24);
    const m = Math.floor((diff / 60000) % 60);
    if (d > 0) return `${d}d ${h}h`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
}

export default function Auctions() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/auctions");
                setItems(data);
            } finally { setLoading(false); }
        })();
        const i = setInterval(async () => {
            try {
                const { data } = await api.get("/auctions");
                setItems(data);
            } catch (_) {}
        }, 15000);
        return () => clearInterval(i);
    }, []);

    return (
        <div className="min-h-screen bg-ink-300 text-silver-100">
            <Navbar />
            <section className="max-w-[1400px] mx-auto px-4 md:px-10 pt-14 pb-28">
                <div className="flex items-center gap-3 mb-3 text-[11px] tracking-[0.3em] text-silver-400 uppercase">
                    <Gavel className="w-4 h-4 text-platinum" /> Live Auctions
                </div>
                <h1 className="font-display text-5xl md:text-6xl leading-none mb-3">
                    The floor is <span className="silver-text italic">open.</span>
                </h1>
                <p className="text-silver-400 max-w-2xl mb-12">
                    Timed auctions on exclusive film copyright. Verified buyers only. Top bid at close enters a 24-hour acceptance window.
                </p>

                {loading ? (
                    <div className="text-silver-500">Loading auctions…</div>
                ) : items.length === 0 ? (
                    <div className="border border-white/10 rounded-sm p-10 text-center text-silver-500">
                        No live auctions right now. Check back soon.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="auctions-grid">
                        {items.map((f, i) => (
                            <motion.div
                                key={f.id}
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.04 }}
                                className="glass border border-white/5 hover:border-platinum/40 rounded-sm overflow-hidden group transition-colors"
                            >
                                <Link to={`/auction/${f.id}`} className="block">
                                    <div className="aspect-[4/5] relative bg-ink-200 overflow-hidden">
                                        {f.poster_url ? (
                                            <img src={f.poster_url} alt={f.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center font-display text-silver-500">{f.title}</div>
                                        )}
                                        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                                        <div className="absolute top-3 left-3">
                                            <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase border border-platinum text-platinum bg-ink-400/80 backdrop-blur-sm px-2 py-1 rounded-sm">
                                                <Gavel className="w-3 h-3" /> {f.bid_count} {f.bid_count === 1 ? "bid" : "bids"}
                                            </span>
                                        </div>
                                        <div className="absolute top-3 right-3">
                                            <span className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase bg-ink-400/80 backdrop-blur-sm border border-white/10 text-silver-200 px-2 py-1 rounded-sm">
                                                <Clock3 className="w-3 h-3" /> {timeLeft(f.auction_ends_at)}
                                            </span>
                                        </div>
                                        <div className="absolute bottom-0 left-0 right-0 p-5">
                                            <div className="font-display text-2xl text-white mb-1">{f.title}</div>
                                            <div className="text-[11px] uppercase tracking-widest text-silver-400">
                                                {f.country} · {f.language}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="p-5 flex items-center justify-between">
                                        <div>
                                            <div className="text-[10px] tracking-widest uppercase text-silver-500">Top bid</div>
                                            <div className="font-display text-2xl silver-text tabular-nums">${Number(f.top_bid_usd || 0).toLocaleString()}</div>
                                        </div>
                                        <div className="inline-flex items-center gap-1 text-platinum text-xs tracking-widest uppercase">
                                            Enter <ArrowUpRight className="w-3.5 h-3.5" />
                                        </div>
                                    </div>
                                </Link>
                            </motion.div>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
